import { BaseLivlyApiResponse } from "@/types/Base";
import {
  LiabilityQuestionItemsProps,
  LibalityQuestionProps,
  UnderwritingQuestions
} from "./liability-questions";
import axios from "axios";
import { BASE_API_URL } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/Button";
import { Spinner } from "@/components/Spinner";
import { RadioGroup } from "@headlessui/react";
import { LivlyDatePicker } from "@/components/DatePicker";
import Alert from "@/components/Alert";
import { useFeatureToggle } from "../features";

const questionNameMap: Record<string, string> = {
  dwellingType: "dwellingType",
  manufacturedMobileHome: "isManufacturedHome",
  coverageMasonry: "isMasonryConstruction",
  mandatoryInsuranceRequirement: "mustHaveInsurance",
  losses: "losses",
  numberOfLosses: "howManyLosses",
  lossDate: "lastLossDate",
  animalInjury: "hadAnimalInjury",
  Arson: "hasStateDisclosure",
  leaseEffectiveDate: "leaseEffectiveDate"
};

const UnderWritingQuestions: React.FC<LibalityQuestionProps> = ({
  user,
  onSubmit,
  isSubmitting,
  isFetching,
  isInsuranceOnly
}) => {
  const navigate = useNavigate();

  const {
    data = [],
    isLoading,
    isFetched,
    isError
  } = useGetLiabilityQuestions(user.userId, user.propertyUnitLeaseId);
  const hasNoQuestionInStorage = localStorage.getItem("noQuestions") === "true";
  //Just for testing purposes, if the feature is enabled, we will show 0 questions
  // This is to simulate the scenario where no questions are available
  const isZeroQuestionsEnabled = useFeatureToggle("assurant-v2");

  useEffect(() => {
    if (hasNoQuestionInStorage) {
      localStorage.removeItem("noQuestions");
      navigate("../insurance-quote");
    }
  }, [hasNoQuestionInStorage, navigate]);

  //Exceptional case: if data is empty, redirect to the summary page
  useEffect(() => {
    if (
      (data.length === 0 || isZeroQuestionsEnabled) &&
      isFetched &&
      !isError &&
      !hasNoQuestionInStorage
    ) {
      localStorage.setItem("noQuestions", "true");
      const options = isInsuranceOnly
        ? {
            state: {
              label: "Insurance",
              noQuestions: true,
              isInsuranceOnly: true
            }
          }
        : { state: { label: "Insurance", noQuestions: true } };
      navigate("../insurance-quote/summary", options);
    }
  }, [data.length, isZeroQuestionsEnabled, isFetched, isError, navigate]);

  const [answers, setAnswers] = useState<Record<string, string>>({});

  const mappedData = data.map((q) => ({
    ...q,
    questionName: questionNameMap[q.questionName] || q.questionName
  }));

  if (isLoading || isFetching) {
    return (
      <div className="flex items-center justify-center flex-1 my-56">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  const handleAnswerChange = (questionName: string, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionName]: value
    }));

    // Special handling for "losses" question to auto-update "numberOfLosses"
    if (questionName === "losses") {
      // Find the numberOfLosses question in the current data
      const numberOfLossesQuestion = mappedData.find(
        (q: UnderwritingQuestions) =>
          q.questionName === "howManyLosses" &&
          q.instructions.includes("capture the selected value")
      );

      if (numberOfLossesQuestion) {
        let numberOfLossesValue = "";
        if (value === "Y") {
          numberOfLossesValue = "1";
        } else if (value === "Y2") {
          numberOfLossesValue = "2";
        } else if (value === "N") {
          numberOfLossesValue = "0";
        }

        setAnswers((prev) => ({
          ...prev,
          howManyLosses: numberOfLossesValue
        }));
      }
    }
    if (["leaseEffectiveDate", "lastLossDate"].includes(questionName)) {
      const pad = (n: number) => String(n).padStart(2, "0");
      const yyyy = value.getFullYear();
      const mm = pad(value.getMonth() + 1);
      const dd = pad(value.getDate());

      setAnswers((prev) => ({
        ...prev,
        [questionName]: `${yyyy}-${mm}-${dd}`
      }));
    }
  };
  const visibleQuestions = mappedData.filter((q) =>
    isQuestionVisible(q, answers)
  );

  const isNextButtonDisabled = visibleQuestions.some(
    (question) => !answers[question.questionName]
  );

  return (
    <>
      <h2 className="font-medium">Liability Questions</h2>
      <p className=" text-gray-500">
        Property loss underwriting questions are required by your state based on
        your selection. Please answer the following questions.
      </p>
      {isError && (
        <Alert
          variant="danger"
          message={`Error fetching questions. Please contact support.`}
        />
      )}
      <div className="mt-4">
        {visibleQuestions.map((question, index) => (
          <LiabilityQuestionItems
            key={question.questionName}
            question={question}
            questionIndex={index}
            answers={answers}
            onAnswerChange={handleAnswerChange}
          />
        ))}
      </div>
      <div className="w-full flex justify-end mt-4">
        <Button
          disabled={isNextButtonDisabled || isSubmitting}
          data-test-id={`next-button`}
          onClick={() =>
            onSubmit(
              Object.entries(answers).map(([questionName, answer]) => ({
                questionName,
                answer: answer === "Y2" ? "Y" : answer // Map Y2 to Y for submission
              }))
            )
          }
        >
          {isSubmitting && <Spinner />}
          {isSubmitting ? "Please wait..." : "Next"}
        </Button>
      </div>
    </>
  );
};

const parseOptionsFromInstructions = (question: UnderwritingQuestions) => {
  const optionMap: Record<string, string> = {};

  // Special case for the specific instruction pattern
  if (
    question.instructions.includes(
      "If clients select '0', send 'N'. For any other option selected (1, 2, or more), send 'Y'."
    )
  ) {
    return {
      N: "0",
      Y: "1",
      //Y2 : "2+" // Use a unique key for the second Y
      Y2: "2+"
    };
  }

  // Match patterns like "A = Apartment/Condominium"
  const matches = question.instructions.match(/([A-Z])\s*=\s*[^,]+/g);

  if (matches) {
    matches.forEach((match) => {
      const [key, label] = match.split("=").map((s) => s.trim());
      if (key && label) {
        optionMap[key] = label;
      }
    });
  }

  // Fallback to allowedResponses if instructions don't contain full labels
  question.allowedResponses.forEach((code) => {
    if (!optionMap[code]) {
      if (code === "Y") optionMap[code] = "Yes";
      else if (code === "N") optionMap[code] = "No";
      else optionMap[code] = code; // default to code if no label found
    }
  });

  return optionMap; // e.g., { A: "Apartment/Condominium", D: "Dormitory/Student Housing" }
};

const minDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 3);
  return d;
})();

const maxDate = new Date();

const LiabilityQuestionItems: React.FC<LiabilityQuestionItemsProps> = ({
  question,
  questionIndex,
  answers,
  onAnswerChange
}) => {
  const options = parseOptionsFromInstructions(question);

  const handleValueChange = (value: string) => {
    onAnswerChange(question.questionName, value);
  };

  return (
    <div className="mb-6" data-test-id="question-item">
      <p
        className="font-medium text-gray-700 mb-2"
        data-test-id={`question-item-${questionIndex}`}
      >
        {questionIndex + 1}. {question.question}
      </p>
      {["lastLossDate", "leaseEffectiveDate"].includes(
        question.questionName
      ) ? (
        <div className="mx-4">
          <LivlyDatePicker
            value={answers[question.questionName]}
            onChange={(date) => {
              if (date) onAnswerChange(question.questionName, date);
            }}
            placeholder={
              question.questionName === "lastLossDate"
                ? "Date of loss"
                : "Select Date"
            }
            minDate={minDate}
            maxDate={
              question.questionName === "lastLossDate" ? maxDate : undefined
            }
          />
        </div>
      ) : (
        <RadioGroup onChange={handleValueChange}>
          <div className="space-y-2">
            {Object.entries(options).map(([value, label]) => (
              <RadioGroup.Option
                key={label}
                value={value}
                className="flex items-center space-x-2 cursor-pointer px-4"
                data-test-id={`options-item-${questionIndex}`}
              >
                {({ checked }) => (
                  <>
                    <span
                      className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        checked ? "border-blue-600" : "border-gray-400"
                      }`}
                    >
                      <span
                        className={`h-2 w-2 rounded-full bg-blue-600 transition ${
                          checked ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </span>
                    <span>{label}</span>
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      )}
    </div>
  );
};

const isQuestionVisible = (
  question: UnderwritingQuestions,
  answers: Record<string, string>
): boolean => {
  const instruction = question.instructions.trim();
  // For FL
  if (instruction.includes(`If losses is 'Y', do not display`)) {
    return false;
  }

  // If the instruction does not start with "Ask", the question is always visible
  if (!instruction.toLowerCase().startsWith("ask")) return true;

  // Check if the question is dependent on dwellingType being "S"
  if (instruction.includes("dwellingType is S")) {
    return answers["dwellingType"] === "S";
  }

  // Check if the question is dependent on losses being "Y"
  if (instruction.includes("losses is Y")) {
    const lossesYes = answers["losses"] === "Y";

    // Further check if numberOfLosses equals to 1
    if (instruction.includes("numberOfLosses equals to 1")) {
      return lossesYes && answers["howManyLosses"] === "1";
    }

    return lossesYes;
  }

  // Default to visible
  return true;
};

function useGetLiabilityQuestions(userId: number, leaseId: number) {
  const getLiabilityQuestions = async () => {
    const result = await axios.get<
      BaseLivlyApiResponse<UnderwritingQuestions[]>
    >(`${BASE_API_URL}/v2/RentersInsurance/GH/Questions/${userId}/${leaseId}`);

    return result.data.Data;
  };

  return useQuery(
    ["liability-questions", userId, leaseId],
    getLiabilityQuestions
  );
}

export default UnderWritingQuestions;
